import { areaList, areaName, type AreaCode } from '@ftthnms/shared';
import { Badge, RegionChip } from '@ftthnms/ui';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Activity,
  Bell,
  Bot,
  ChevronDown,
  History,
  House,
  LayoutDashboard,
  List,
  LogOut,
  Map as MapIcon,
  Monitor,
  Moon,
  Route,
  SlidersHorizontal,
  Sun,
  Upload,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError } from '../lib/api';
import { useIncidentSummary } from '../lib/incidents';
import { ROLE_LABEL, SessionProvider, useAreaDirectory, useMe, useSession } from '../lib/session';
import { LogoutDialog } from './LogoutDialog';
import { ToastProvider } from './Toast';
import { RealtimeProvider } from '../lib/realtime';
import { setThemePref, useTheme, type ThemePref } from '../lib/theme';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Shown only when the ability allows it. */
  visible?: (s: ReturnType<typeof useSession>) => boolean;
}

const NAV: Array<{ group: string; items: NavItem[] }> = [
  {
    group: 'Operasional',
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/map', label: 'Peta Jaringan', icon: MapIcon },
      { to: '/assets', label: 'Aset', icon: List },
      { to: '/routes', label: 'Jalur Kabel', icon: Route },
      { to: '/customers', label: 'Pelanggan', icon: House, visible: (s) => s.ability.can('read', 'Asset') },
    ],
  },
  {
    group: 'Monitoring',
    items: [
      {
        to: '/monitoring',
        label: 'Status Perangkat',
        icon: Activity,
        visible: (s) => s.ability.can('read', 'Monitoring'),
      },
      { to: '/alerts', label: 'Insiden', icon: Bell, visible: (s) => s.ability.can('read', 'Alert') },
    ],
  },
  {
    group: 'Perencanaan',
    items: [
      { to: '/cost', label: 'Biaya & BOQ', icon: Wallet, visible: (s) => s.ability.can('read', 'Pricing') },
      {
        to: '/import-export',
        label: 'Import / Export',
        icon: Upload,
        visible: (s) => s.ability.can('export', 'Asset'),
      },
    ],
  },
  {
    group: 'Sistem',
    items: [
      {
        to: '/users',
        label: 'Pengguna & Peran',
        icon: Users,
        visible: (s) => s.ability.can('manage', 'User'),
      },
      { to: '/audit', label: 'Audit Log', icon: History, visible: (s) => s.ability.can('read', 'AuditLog') },
      {
        to: '/settings',
        label: 'Pengaturan',
        icon: SlidersHorizontal,
        visible: (s) => s.ability.can('manage', 'Settings') || s.ability.can('manage', 'MasterData'),
      },
      { to: '/ai-agent', label: 'AI Agent', icon: Bot, visible: (s) => s.ability.can('use', 'Mcp') },
    ],
  },
];

const PAGE_TITLES: Record<string, { eyebrow: string; title: string }> = {
  '/': { eyebrow: 'Operasional', title: 'Dashboard' },
  '/map': { eyebrow: 'Operasional', title: 'Peta Jaringan' },
  '/assets': { eyebrow: 'Operasional', title: 'Aset' },
  '/routes': { eyebrow: 'Operasional', title: 'Jalur Kabel' },
  '/import-export': { eyebrow: 'Perencanaan', title: 'Import / Export' },
  '/customers': { eyebrow: 'Operasional', title: 'Pelanggan' },
  '/monitoring': { eyebrow: 'Monitoring', title: 'Status Perangkat' },
  '/alerts': { eyebrow: 'Monitoring', title: 'Insiden' },
  '/cost': { eyebrow: 'Perencanaan', title: 'Biaya & BOQ' },
  '/users': { eyebrow: 'Sistem', title: 'Pengguna & Peran' },
  '/audit': { eyebrow: 'Sistem', title: 'Audit Log' },
  '/settings': { eyebrow: 'Sistem', title: 'Pengaturan' },
  '/ai-agent': { eyebrow: 'Sistem', title: 'AI Agent (MCP)' },
};

/** Authenticated layout: loads /me, redirects to /login when there is no session. */
export function AppShell() {
  const me = useMe();
  const directory = useAreaDirectory();
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    // Guard: the path briefly becomes /login while this shell is still mounted.
    if (me.error instanceof ApiError && me.error.status === 401 && !path.startsWith('/login')) {
      void navigate({ to: '/login', search: { returnTo: path, error: undefined }, replace: true });
    }
  }, [me.error, navigate, path]);

  if (
    me.isPending ||
    (me.error instanceof ApiError && me.error.status === 401) ||
    (me.data && directory.isPending)
  ) {
    return (
      <div className="grid h-full place-items-center">
        <div className="flex flex-col items-center gap-3">
          <span className="fn-spinner" aria-label="Memuat" />
          {me.failureCount > 0 && <span className="fn-hint">Menghubungkan ke server FTTH NMS…</span>}
        </div>
      </div>
    );
  }
  if (me.error) {
    return (
      <div className="grid h-full place-items-center p-6">
        <div className="fn-alert fn-alert--danger" role="alert" style={{ maxWidth: 480 }}>
          <div className="fn-alert__body">
            <span className="fn-alert__title">Tidak bisa terhubung ke server FTTH NMS</span>
            <span className="fn-alert__text">
              {me.error.message} Muat ulang halaman, atau hubungi Admin jika masih terjadi.
            </span>
          </div>
        </div>
      </div>
    );
  }

  const compact = path === '/map';
  return (
    <SessionProvider me={me.data}>
      <ToastProvider>
        <RealtimeProvider>
          {/* Fixed shell: only <main> scrolls, the sidebar and topbar stay put. */}
          <div
            className="grid h-full overflow-hidden"
            style={{ gridTemplateColumns: 'auto minmax(0, 1fr)', gridTemplateRows: 'minmax(0, 1fr)' }}
          >
            <Sidebar compact={compact} path={path} />
            <div className="flex min-h-0 min-w-0 flex-col">
              <Topbar path={path} />
              <main className="min-h-0 flex-1 overflow-auto">
                <Outlet />
              </main>
            </div>
          </div>
        </RealtimeProvider>
      </ToastProvider>
    </SessionProvider>
  );
}

function Sidebar({ compact, path }: { compact: boolean; path: string }) {
  const session = useSession();
  const openIncidents = useIncidentSummary().data?.open ?? 0;
  return (
    <aside
      className={`fn-sidebar ${compact ? 'fn-sidebar--rail' : ''}`}
      style={{ height: '100%', overflowY: 'auto' }}
    >
      <div className="fn-sidebar__brand">
        <img className="fn-logo" src="/logo-only.png" alt="Speedmax" />
        <div className="fn-wordmark">
          <span className="fn-wordmark__name">
            FTTH <span>NMS</span>
          </span>
          <span className="fn-wordmark__sub">Speedmax Network</span>
        </div>
      </div>
      <nav className="fn-nav" aria-label="Menu utama">
        {NAV.map((g) => {
          const items = g.items.filter((i) => !i.visible || i.visible(session));
          if (items.length === 0) return null;
          return (
            <div key={g.group} style={{ display: 'contents' }}>
              <div className="fn-nav__label">{g.group}</div>
              {items.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.label}
                    to={item.to}
                    className="fn-nav__item"
                    aria-label={compact ? item.label : undefined}
                    title={compact ? item.label : undefined}
                    aria-current={path === item.to ? 'page' : undefined}
                  >
                    <Icon className="i" aria-hidden />
                    <span>{item.label}</span>
                    {item.to === '/alerts' && openIncidents > 0 && (
                      <Badge tone="danger" square>
                        {openIncidents}
                      </Badge>
                    )}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}

function Topbar({ path }: { path: string }) {
  const { me, area, setArea, areaOptions, areaLocked } = useSession();
  const heading = PAGE_TITLES[path] ?? { eyebrow: 'FTTH NMS', title: '' };
  const technicianAreas = areaOptions === 'all' ? [] : areaOptions;
  const pickable: readonly AreaCode[] = areaOptions === 'all' ? areaList().map((a) => a.code) : areaOptions;
  const [logoutOpen, setLogoutOpen] = useState(false);

  return (
    <header className="fn-topbar">
      <div className="fn-topbar__title">
        <span className="fn-topbar__eyebrow">{heading.eyebrow}</span>
        <h1 className="fn-topbar__h">{heading.title}</h1>
      </div>
      <div className="fn-topbar__spacer" />
      <div className="fn-topbar__actions">
        {areaLocked ? (
          <RegionChip code={technicianAreas[0]!} />
        ) : (
          <Menu
            label="Pilih wilayah"
            trigger={
              <span className="fn-picker">
                <RegionChip
                  code={area ?? 'all'}
                  label={
                    area ? areaName(area) : areaOptions === 'all' ? 'Semua wilayah' : 'Semua wilayah saya'
                  }
                />
                <ChevronDown className="i i--sm" aria-hidden />
              </span>
            }
          >
            {(close) => (
              <>
                <MenuItem onClick={() => (setArea(null), close())} active={!area}>
                  <RegionChip
                    code="all"
                    label={areaOptions === 'all' ? 'Semua wilayah' : 'Semua wilayah saya'}
                  />
                </MenuItem>
                {pickable.map((code) => (
                  <MenuItem key={code} onClick={() => (setArea(code), close())} active={area === code}>
                    <RegionChip code={code} />
                  </MenuItem>
                ))}
              </>
            )}
          </Menu>
        )}
        <IncidentBell />
        <ThemeMenu />
        <Menu
          label="Menu akun"
          align="right"
          trigger={
            <span className="fn-account">
              <span className="fn-avatar">{initials(me.user.name)}</span>
              <span>
                <span className="fn-account__name">{me.user.name}</span>
                <span className="fn-account__role">{roleLine(me)}</span>
              </span>
            </span>
          }
        >
          {(close) => (
            <>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
                <div className="fn-account__name">{me.user.name}</div>
                <div className="mono" style={{ color: 'var(--text-muted)' }}>
                  @{me.user.username}
                </div>
              </div>
              <MenuItem onClick={() => window.open('https://auth.speedmax.id', '_blank', 'noopener')}>
                Profil di Speedmax ID
              </MenuItem>
              <MenuItem danger onClick={() => (close(), setLogoutOpen(true))}>
                <LogOut className="i i--sm" aria-hidden /> Keluar…
              </MenuItem>
            </>
          )}
        </Menu>
      </div>
      <LogoutDialog open={logoutOpen} onClose={() => setLogoutOpen(false)} />
    </header>
  );
}

/** Open incidents in the user's areas; links to the incident list. */
function IncidentBell() {
  const { ability } = useSession();
  const open = useIncidentSummary().data?.open ?? 0;
  if (!ability.can('read', 'Alert')) return null;
  const label = open > 0 ? `${open} insiden aktif` : 'Tidak ada insiden aktif';
  return (
    <Link to="/alerts" className="fn-icon-btn fn-notify" aria-label={label} title={label}>
      <Bell className="i" aria-hidden />
      {open > 0 && <span className="fn-notify__count">{open > 99 ? '99+' : open}</span>}
    </Link>
  );
}

function ThemeMenu() {
  const { pref, theme } = useTheme();
  const options: Array<{ value: ThemePref; label: string; icon: LucideIcon }> = [
    { value: 'dark', label: 'Gelap', icon: Moon },
    { value: 'light', label: 'Terang', icon: Sun },
    { value: 'system', label: 'Ikuti sistem', icon: Monitor },
  ];
  const Current = theme === 'dark' ? Moon : Sun;
  return (
    <Menu
      label="Tema tampilan"
      align="right"
      trigger={
        <span className="fn-icon-btn">
          <Current className="i" aria-hidden />
        </span>
      }
    >
      {(close) =>
        options.map(({ value, label, icon: Icon }) => (
          <MenuItem key={value} active={pref === value} onClick={() => (setThemePref(value), close())}>
            <Icon className="i i--sm" aria-hidden /> {label}
          </MenuItem>
        ))
      }
    </Menu>
  );
}

function Menu({
  label,
  trigger,
  children,
  align = 'left',
}: {
  label: string;
  trigger: ReactNode;
  children: (close: () => void) => ReactNode;
  align?: 'left' | 'right';
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
      >
        {trigger}
      </button>
      {open && (
        <div
          role="menu"
          className="fn-panel"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            [align]: 0,
            minWidth: 220,
            padding: 6,
            zIndex: 50,
            background: 'var(--bg-surface)',
          }}
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

function MenuItem({
  children,
  onClick,
  active,
  danger,
}: {
  children: ReactNode;
  onClick: () => void;
  active?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className="fn-layer"
      onClick={onClick}
      style={{
        fontWeight: active ? 700 : 500,
        color: danger ? 'var(--danger-text)' : undefined,
        background: active ? 'var(--fill-muted)' : undefined,
      }}
    >
      {children}
    </button>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
}

function roleLine(me: ReturnType<typeof useSession>['me']): string {
  if (!me.primaryRole) return '';
  if (me.primaryRole !== 'teknisi') return ROLE_LABEL[me.primaryRole];
  const areas = me.user.grants.filter((g) => g.area).map((g) => areaName(g.area!));
  return `Teknisi ${areas.join(', ')}`;
}
